import { useStats, useStatsT } from './statsContext'
import { Delta, ErrorLine, ListBlock, Loading, Pills, TableHead, UserCell } from './StatsParts'
import { StatsChart } from './StatsChart'
import {
  browserTimeZone,
  fanSums,
  HEAT_SLOTS,
  heatmap,
  hourLabel,
  int,
  listKeyOf,
  money,
  offsetLoader,
  pillLabel,
  PILLS,
  TOP_FAN_SUMS,
  withRange,
  type Raw,
} from './statisticsData'
import { combined, useStat, useStatList } from './statsStore'

export function SubscriptionsMain() {
  const { t } = useStatsT()
  const { store, range, pills, setPill } = useStats()
  const by = pills.subscriptions
  const chart = useStat(store, 'subscriptions/subscribers/chart', withRange(range, { by }))
  const listKey = listKeyOf(`subscriptions/subscribers/latest:${by}`, range.startDate, range.endDate)
  const list = useStatList(store, listKey, offsetLoader(store.call, 'subscriptions/subscribers/latest', withRange(range, { by }), 'users'))
  const d = chart.data ?? {}
  return (
    <>
      <Pills items={PILLS.subscriptions} active={by} onPick={(id) => setPill('subscriptions', id)} label={t('l.subscriptions')} />
      <div className="ofx-block">
        {chart.status === 'loading' ? (
          <Loading />
        ) : chart.status === 'error' ? (
          <ErrorLine message={chart.error} />
        ) : (
          <>
            <div className="ofx-chart-head">
              <strong>
                {t('countSubscribers', { n: int(d.subscribers, false) })}, {money(d.total)}
              </strong>
              <Delta value={d.delta} />
            </div>
            <StatsChart main={{ points: d.earnings, kind: 'money', label: t('l.earnings') }} sub={{ points: d.subscribes, kind: 'count', label: t('l.subscribers') }} />
          </>
        )}
      </div>
      <div className="ofx-block ofx-list">
        <ListBlock
          listKey={listKey}
          list={list}
          empty={t('empty.subscribers')}
          header={
            <TableHead cols="fans">
              <span>{t('l.name')}</span>
              <span>{t('l.price')}</span>
              <span className="num">{t('l.totalDuration')}</span>
            </TableHead>
          }
        >
          {list.rows.map((user, i) => {
            const s = fanSums(user)
            const price = Number(s.price ?? user.currentSubscribePrice ?? 0)
            return (
              <div key={String(user.id ?? i)} className="ofx-row">
                <div className="ofx-row-grid ofx-cols-fans">
                  <UserCell user={user} />
                  <span>{price > 0 ? money(price) : t('free')}</span>
                  <span className="num">{String(s.duration || user.subscribedOnDuration || '')}</span>
                </div>
              </div>
            )
          })}
        </ListBlock>
      </div>
    </>
  )
}

export function TopFansMain() {
  const { t } = useStatsT()
  const { store, range, pills, setPill } = useStats()
  const by = pills.topFans
  const label = t(`l.${pillLabel(PILLS.topFans, by)}`)
  const listKey = listKeyOf(`subscriptions/subscribers/top:${by}`, range.startDate, range.endDate)
  const list = useStatList(store, listKey, offsetLoader(store.call, 'subscriptions/subscribers/top', withRange(range, { by }), 'users'))
  const field = TOP_FAN_SUMS[by] ?? 'totalSumm'
  return (
    <>
      <Pills items={PILLS.topFans} active={by} onPick={(id) => setPill('topFans', id)} label={t('l.topFans')} />
      <div className="ofx-block ofx-list">
        <ListBlock
          listKey={listKey}
          list={list}
          empty={t('empty.data')}
          header={
            <TableHead cols="top">
              <span>{t('l.name')}</span>
              <span className="num">{by === 'total' ? t('l.total') : `${label} / ${t('l.total')}`}</span>
            </TableHead>
          }
        >
          {list.rows.map((user, i) => {
            const s = fanSums(user)
            return (
              <div key={String(user.id ?? i)} className="ofx-row">
                <div className="ofx-row-grid ofx-cols-top">
                  <UserCell user={user} />
                  <span className="num">
                    {by === 'total' ? (
                      <strong>{money(s.totalSumm)}</strong>
                    ) : (
                      <>
                        <strong>{money(s[field])}</strong>
                        <small> / {money(s.totalSumm)}</small>
                      </>
                    )}
                  </span>
                </div>
              </div>
            )
          })}
        </ListBlock>
      </div>
    </>
  )
}

export function ActivityMain() {
  const { t, intl } = useStatsT()
  const { store, range } = useStats()
  const tz = browserTimeZone()
  const table = useStat(store, 'fans-activity/list', withRange(range, { format: 'table', timeZone: tz }))
  const hours = useStat(store, 'fans-activity/list', withRange(range, { format: 'hours', timeZone: tz }))
  const state = combined(table, hours)
  if (state.loading) return <Loading />
  if (state.error) return <ErrorLine message={state.error} />
  const heat = heatmap((table.data as Raw | null)?.data)
  const dayLabel = (i: number) => new Date(Date.UTC(2026, 1, 1 + i)).toLocaleDateString(intl, { weekday: 'short', timeZone: 'UTC' })
  const bars = Array.isArray((hours.data as Raw | null)?.data) ? ((hours.data as Raw).data as Raw[]) : []
  return (
    <>
      <div className="ofx-block">
        <div className="ofx-subhead">{t('subscribersActivity')}</div>
        <div className="ofx-heat" role="table" aria-label={t('subscribersActivity')}>
          {heat.map((row, i) => (
            <div key={i} className="ofx-heat-row" role="row">
              <span className="ofx-heat-day" role="rowheader">
                {dayLabel(i)}
              </span>
              {row.map((cell, j) => {
                const h = HEAT_SLOTS[j] ?? 0
                const label = `${dayLabel(i)} ${hourLabel(h, intl)}–${hourLabel((h + 3) % 24, intl)}: ${Math.round(cell.ratio * 100)}%`
                return <span key={h} role="cell" className="ofx-heat-cell" style={{ ['--lvl' as string]: cell.level.toFixed(3) }} title={label} aria-label={label} />
              })}
            </div>
          ))}
          <div className="ofx-heat-row" aria-hidden="true">
            <span />
            {HEAT_SLOTS.map((h) => (
              <span key={h} className="ofx-heat-hour">
                {hourLabel(h, intl)}
              </span>
            ))}
          </div>
        </div>
      </div>
      <div className="ofx-block">
        <div className="ofx-subhead">{t('mostActiveHours')}</div>
        <div className="ofx-hours">
          <div className="ofx-hours-y" aria-hidden="true">
            <span>100%</span>
            <span>66%</span>
            <span>33%</span>
          </div>
          <div className="ofx-hours-bars">
            {bars.map((h, i) => {
              const pct = Math.round((Number(h?.ratio) || 0) * 100)
              const label = `${hourLabel(i, intl)}: ${pct}%`
              return <span key={i} className="ofx-hours-bar" style={{ height: `${pct}%` }} title={label} aria-label={label} />
            })}
          </div>
          <div className="ofx-hours-x" aria-hidden="true">
            {HEAT_SLOTS.map((h) => (
              <span key={h}>{hourLabel(h, intl)}</span>
            ))}
          </div>
        </div>
      </div>
    </>
  )
}
